import React from "react";
import { formatTime } from "../../hooks/useClock";
import { PALETTE, TAKEN } from "../../data/orbstack";
import node from "../../assets/icons/orbstack/node.png";
import redis from "../../assets/icons/orbstack/redis.png";
import postgres from "../../assets/icons/orbstack/postgres.png";
import apache from "../../assets/icons/orbstack/apache.png";
import { ReactComponent as BoxIcon } from "../../assets/svgs/sf-symbols/orbstack/shippingbox.fill.svg";
import { ReactComponent as ChevronRight } from "../../assets/svgs/sf-symbols/orbstack/chevron.right.svg";
import { ReactComponent as DriveIcon } from "../../assets/svgs/sf-symbols/orbstack/externaldrive.fill.svg";

const LOGOS = { node, redis, postgres, apache };
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Sizes as macOS writes them: "659.2 MB", "298 MB", "Zero KB", "14.36 GB".
export const formatSize = (mb) => {
  if (!mb) return "Zero KB";
  return mb >= 1000 ? `${parseFloat((mb / 1000).toFixed(2))} GB` : `${parseFloat(mb.toFixed(1))} MB`;
};

// How long before the screenshots something was created, in days.
const daysOld = (created) => Math.floor((TAKEN - Date.parse(created)) / 86400000);

// "4 days ago", "2 weeks ago", "11 months ago", "2 years ago".
export const formatAgo = (created) => {
  const days = daysOld(created);
  const [n, unit] =
    days < 7
      ? [days, "day"]
      : days < 30
        ? [Math.floor(days / 7), "week"]
        : days < 365
          ? [Math.floor(days / 30), "month"]
          : [Math.floor(days / 365), "year"];
  return `${n} ${unit}${n === 1 ? "" : "s"} ago`;
};

// "1 month ago (Sep 17, 2026 at 9:30 PM)", moved along to the visit by whole days.
export const formatCreated = (created) => {
  const d = new Date(Date.parse(created) + Math.round((Date.now() - TAKEN) / 86400000) * 86400000);
  return `${formatAgo(created)} (${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} at ${formatTime(d)})`;
};

// A color from OrbStack's palette for something it has no logo or color for, the same each time.
const hashLook = (name) => {
  const looks = Object.keys(PALETTE);
  const hash = [...name].reduce((h, ch) => (h * 31 + ch.charCodeAt(0)) >>> 0, 7);
  return looks[hash % looks.length];
};

// The image a container runs, as its `image` names it ("postgres" is "postgres:latest").
export const imageFor = (images, ref) => images.find((i) => i.ref === ref || i.ref === `${ref}:latest`) || null;

export const lookOf = (images, ref) => {
  const image = imageFor(images, ref);
  return image ? image.look : hashLook(ref);
};

// An image's or container's icon: OrbStack's logo for it, or a box on a colored circle. A container
// shows whether it's running with a dot.
export const Thing = ({ look, status, className = "" }) => (
  <span className={`ob-thing ${className}`}>
    {LOGOS[look] ? (
      <img src={LOGOS[look]} alt="" draggable={false} className="ob-icon" />
    ) : (
      <span className="ob-icon ob-icon--box" style={{ background: PALETTE[look] }}>
        <BoxIcon />
      </span>
    )}
    {status && (
      <span className={`ob-dot ob-dot--${status}`} aria-label={status === "running" ? "Running" : "Stopped"} />
    )}
  </span>
);

// The name as OrbStack shows it: the repository and tag in gray around the image's name.
export const ImageName = ({ image }) => {
  const [path, tag] = image.ref.split(":");
  const slash = path.lastIndexOf("/");
  return (
    <>
      {slash >= 0 && <span className="ob-dim">{path.slice(0, slash + 1)}</span>}
      {path.slice(slash + 1)}
      <span className="ob-dim">:{tag}</span>
    </>
  );
};

// A volume's icon: a drive in its color.
export const Drive = ({ look }) => (
  <DriveIcon className="ob-drive" style={{ color: PALETTE[look] }} aria-hidden="true" />
);

export const Chevron = () => <ChevronRight className="ob-chevron" aria-hidden="true" />;
